import { useEffect, useRef } from "react";
import { driver } from "driver.js";
// driver.css is imported in src/index.js before index.css so our brand
// overrides in index.css always win on specificity / order.

/**
 * MyTACS interactive product tour using driver.js.
 *
 * Spotlights real UI elements on the dashboard / sidebar / header and explains
 * them in-context. Falls back gracefully if a target is missing (e.g. nav-item
 * hidden because of role restrictions).
 *
 * Props (compatible with old TutorialModal):
 *   - open: boolean — when transitions to true, the tour starts
 *   - onOpenChange(false): called when the tour ends or is cancelled
 *   - onDismiss(): called when user opts in to "don't show again"
 */
export default function TutorialModal({ open, onOpenChange, onDismiss }) {
    const driverRef = useRef(null);
    // Stash the latest callbacks in refs so the start-effect can stay deps-stable.
    const onOpenChangeRef = useRef(onOpenChange);
    const onDismissRef = useRef(onDismiss);
    useEffect(() => { onOpenChangeRef.current = onOpenChange; }, [onOpenChange]);
    useEffect(() => { onDismissRef.current = onDismiss; }, [onDismiss]);

    useEffect(() => {
        if (!open) return undefined;

        // Only include steps whose target actually exists right now.
        // Welcome / final intro steps have no target — they always show.
        const candidate = [
            {
                title: "Willkommen bei MyTACS",
                description:
                    "Das neue ERP-System der DuTACS GmbH. Diese kurze Tour zeigt dir die wichtigsten Bereiche — etwa 2 Minuten. Du kannst sie jederzeit über das ?-Symbol oben rechts erneut starten.",
                // No element → centered overlay
            },
            {
                el: '[data-testid="sidebar"]',
                title: "Navigation",
                description:
                    "Hier links findest du alle Module. Du kannst die Sidebar mit dem Pfeil-Knopf oben einklappen, wenn du mehr Platz brauchst.",
                side: "right",
            },
            {
                el: '[data-testid="nav-dashboard"]',
                title: "Übersicht",
                description:
                    "Deine Startseite. Heute fällige Aufgaben, Stempeluhr, gebuchte Stunden und der Aufgabenfluss in einem Blick.",
                side: "right",
            },
            {
                el: '[data-testid="dashboard-new-task-btn"]',
                title: "Neue Aufgabe",
                description:
                    "Klick auf den großen + Button, um eine Aufgabe anzulegen. Du kannst Verantwortliche zuweisen, Dateien anhängen, Deadlines und Erinnerungen setzen — oder den Mikrofon-Knopf für Spracheingabe nutzen.",
                side: "bottom",
                align: "end",
            },
            {
                el: '[data-testid="kanban-board"]',
                title: "Aufgabenfluss (Kanban)",
                description:
                    "Aufgaben wandern von Erstellt → Übernommen → In Bearbeitung → Erledigt. Per Drag&Drop verschieben. Suchfeld oben filtert blitzschnell.",
                side: "top",
            },
            {
                el: '[data-testid="my-tasks-panel"]',
                title: "Meine Aufgaben",
                description:
                    "Rechts siehst du sofort, was DIR zugewiesen wurde. Sortiert nach Deadline und Priorität.",
                side: "left",
            },
            {
                el: '[data-testid="nav-calendar"]',
                title: "Kalender",
                description:
                    "Monats-, Wochen- und Tagesansicht. Aufgaben mit Deadline, Urlaubsanträge, Schichten und Microsoft-365-Termine erscheinen automatisch. Geburtstage sind farblich markiert.",
                side: "right",
            },
            {
                el: '[data-testid="nav-employees"]',
                title: "Mitarbeiter",
                description:
                    "Personalakten: Stammdaten, Verträge, Lohn, Urlaubskonto, Krankheitstage. Admins können hier auch neue Mitarbeiter anlegen.",
                side: "right",
            },
            {
                el: '[data-testid="nav-vacations"]',
                title: "Urlaubsanträge",
                description:
                    "Antrag stellen, Status verfolgen, Vertretung wählen. Admins genehmigen direkt aus der Liste.",
                side: "right",
            },
            {
                el: '[data-testid="nav-timetrack"]',
                title: "Zeiterfassung",
                description:
                    "Die digitale Stempeluhr. Einchecken, Pause, Auschecken — Standort wählbar (Büro, Außendienst). Admin kann Einträge nachträglich korrigieren mit Audit-Trail.",
                side: "right",
            },
            {
                el: '[data-testid="nav-shifts"]',
                title: "Schichtplan",
                description:
                    "Klick im Wochen-Raster eine Zelle, um eine Schicht anzulegen. Bulk-Modus für mehrere Tage gleichzeitig. Pausen-Vorschlag nach ArbZG §4 mit einem Klick. Soll/Ist-Vergleich erfolgt automatisch.",
                side: "right",
            },
            {
                el: '[data-testid="nav-cases"]',
                title: "Cases & Support",
                description:
                    "Hier landen Fragen, Bugs und Wünsche, die du über die Hilfe-Funktion einreichst. Das Owner-Team antwortet direkt.",
                side: "right",
            },
            {
                el: '[data-testid="nav-drive"]',
                title: "Drive",
                description:
                    "Alle Dateien an einem Ort — gespeichert in deinem Hetzner-Storage in Deutschland, DSGVO-konform. Drag&Drop hochladen, Ordner anlegen, Freigaben pro Person.",
                side: "right",
            },
            {
                el: '[data-testid="nav-announcements"]',
                title: "Neuigkeiten",
                description:
                    "Firmen-Mitteilungen vom Management. Dringende Hinweise blockieren die UI, bis du sie bestätigt hast.",
                side: "right",
            },
            {
                el: '[data-testid="nav-mail"]',
                title: "E-Mail (Microsoft 365)",
                description:
                    "Vollständiger Outlook-Ersatz: gemeinsame Postfächer, KI-Klassifizierung von Reiseanfragen, automatische Aufgabe aus relevanten Mails. Antworten und Weiterleiten direkt aus MyTACS.",
                side: "right",
            },
            {
                el: '[data-testid="nav-claude"]',
                title: "Claude AI",
                description:
                    "Dein KI-Assistent für lange Texte, Übersetzungen, Recherche. Threads kannst du mit Kolleg:innen teilen.",
                side: "right",
            },
            {
                el: '[data-testid="nav-notes"]',
                title: "Notizblock",
                description:
                    "Schnelle Markdown-Notizen mit Tags und Suche. PIN-geschützte Bereiche für sensible Inhalte. PDFs mit Firmen-Briefkopf in einem Klick.",
                side: "right",
            },
            {
                el: '[data-testid="nav-settings"]',
                title: "Einstellungen",
                description:
                    "Profilbild, Passwort, 2-Faktor-Authentifizierung, Benachrichtigungs-Vorlieben und persönliche Einstellungen. Aktiviere 2FA direkt nach diesem Tutorial!",
                side: "right",
            },
            {
                el: '[data-testid="header-lang-de"]',
                title: "Sprache wechseln",
                description:
                    "Drei Sprachen: Deutsch, Englisch, Türkisch. Wirkt sofort — UI, E-Mail-Templates und Datumsformat.",
                side: "bottom",
            },
            {
                el: '[data-testid="open-new-announcement-btn"]',
                title: "Neuigkeit verfassen",
                description:
                    "Admins können hier Mitteilungen an einzelne Personen, Rollen oder die ganze Firma schicken. Optional dringend (blockierend) oder per E-Mail zusätzlich.",
                side: "bottom",
            },
            {
                el: '[data-testid="help-trigger"]',
                title: "Hilfe-Knopf",
                description:
                    "Das Fragezeichen führt dich zum Hilfe-Panel: KI-Chat (kennt MyTACS auswendig), Tutorial wiederholen oder einen Case öffnen.",
                side: "bottom",
            },
            {
                el: '[data-testid="notification-trigger"]',
                title: "Benachrichtigungen",
                description:
                    "Hier erscheinen Aufgaben-Zuweisungen, @-Erwähnungen, Freigaben und dringende Mitteilungen. Roter Punkt = ungelesen.",
                side: "bottom",
            },
            {
                el: '[data-testid="sidebar-user"]',
                title: "Profil & Abmelden",
                description:
                    "Unten links siehst du deinen Account. Klick aufs Profilbild oder über Einstellungen kannst du Passwort + 2FA verwalten. Der Türknopf meldet dich ab.",
                side: "right",
            },
            {
                title: "Geschafft!",
                description:
                    "Du bist startklar. Aktiviere als Erstes 2FA in den Einstellungen, damit dein Account abgesichert ist. Bei Fragen → Klick aufs Fragezeichen oben rechts. Viel Erfolg mit MyTACS!",
                showDontShowAgain: true,
            },
        ];

        // Filter out steps whose element doesn't exist (e.g. role-restricted nav)
        const steps = candidate
            .filter((s) => !s.el || document.querySelector(s.el))
            .map(({ el, title, description, side, align, showDontShowAgain }) => {
                const popover = {
                    title,
                    description: showDontShowAgain
                        ? `${description}<br><br><label class="mytacs-tour-checkbox"><input type="checkbox" id="mytacs-tour-dont-show"> Beim nächsten Login nicht mehr automatisch zeigen</label>`
                        : description,
                    side: side || "bottom",
                    align: align || "center",
                };
                if (el) return { element: el, popover };
                return { popover };
            });

        const d = driver({
            showProgress: true,
            allowClose: true,
            overlayOpacity: 0.6,
            stagePadding: 6,
            stageRadius: 8,
            popoverClass: "mytacs-tour-popover",
            progressText: "Schritt {{current}} von {{total}}",
            nextBtnText: "Weiter →",
            prevBtnText: "← Zurück",
            doneBtnText: "Los geht's",
            steps,
            onDestroyStarted: () => {
                // Called when user clicks X or presses Escape OR finishes tour.
                const cb = document.getElementById("mytacs-tour-dont-show");
                if (cb && cb.checked && onDismissRef.current) onDismissRef.current();
                d.destroy();
            },
            onDestroyed: () => {
                if (onOpenChangeRef.current) onOpenChangeRef.current(false);
            },
        });

        driverRef.current = d;
        // Defer start by one frame so target elements are mounted
        const id = requestAnimationFrame(() => d.drive());

        return () => {
            cancelAnimationFrame(id);
            if (driverRef.current) {
                try { driverRef.current.destroy(); } catch { /* ignore */ }
                driverRef.current = null;
            }
        };
    }, [open]); // intentionally only depend on `open`; callbacks read from refs

    return null;
}
